import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Camera, Trophy, Users, Rocket, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface OnboardingCarouselProps {
  onComplete: () => void;
  onSkip: () => void;
}

const slideIcons = [Sparkles, Camera, Trophy, Users, Rocket];

const slideTitleKeys = [
  "onboarding.carousel.welcomeTitle",
  "onboarding.carousel.captureTitle",
  "onboarding.carousel.scoreTitle",
  "onboarding.carousel.connectTitle",
  "onboarding.carousel.getStartedTitle",
] as const;

const slideDescKeys = [
  "onboarding.carousel.welcomeDesc",
  "onboarding.carousel.captureDesc",
  "onboarding.carousel.scoreDesc",
  "onboarding.carousel.connectDesc",
  "onboarding.carousel.getStartedDesc",
] as const;

const slideTitleDefaults = [
  "Welcome to AGXL",
  "Capture Farm Activities",
  "Build Your Score",
  "Connect with Farmers",
  "Get Started",
];

const slideDescDefaults = [
  "Your partner in building farm credit through daily activities",
  "Take photos or use voice notes to log your daily farm work instantly",
  "Earn points for every activity, complete challenges, and climb the leaderboard",
  "Join a network of farmers, share knowledge, and learn best practices",
  "Begin your journey to better farm management and improved credit access",
];

export const OnboardingCarousel = ({ onComplete, onSkip }: OnboardingCarouselProps) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleNext = () => {
    if (currentSlide === slideIcons.length - 1) {
      onComplete();
    } else {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      {/* Skip Button */}
      <div className="absolute top-4 right-4 z-10">
        <Button onClick={onSkip} variant="ghost" size="sm">
          {t("onboarding.valueStory.skip")}
        </Button>
      </div>

      {/* Slide Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div
          key={currentSlide}
          className="w-full max-w-md text-center animate-fade-in"
        >
          <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-primary/10 flex items-center justify-center">
            {(() => {
              const Icon = slideIcons[currentSlide];
              return <Icon className={cn("w-16 h-16", "text-primary")} />;
            })()}
          </div>

          <h2 className="text-3xl font-bold mb-4 text-foreground">
            {t(slideTitleKeys[currentSlide], { defaultValue: slideTitleDefaults[currentSlide] })}
          </h2>

          <p className="text-lg text-muted-foreground mb-8">
            {t(slideDescKeys[currentSlide], { defaultValue: slideDescDefaults[currentSlide] })}
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="px-6 pb-8 space-y-6">
        {/* Pagination Dots */}
        <div className="flex justify-center gap-2">
          {slideIcons.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all",
                currentSlide === index 
                  ? "bg-primary w-8" 
                  : "bg-muted-foreground/30"
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {currentSlide > 0 && (
            <Button
              onClick={handlePrevious}
              variant="outline"
              size="lg"
              className="flex-1"
            >
              {t("common.back")}
            </Button>
          )}
          
          <Button
            onClick={handleNext}
            variant="primary"
            size="lg"
            className="flex-1"
          >
            {currentSlide === slideIcons.length - 1 ? t("common.done") : t("common.next")}
          </Button>
        </div>
      </div>
    </div>
  );
};
